import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';

const GuideNavWeek = React.forwardRef(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn(
      'c-guide-nav-week',
      // Estilos base
      'inline-flex max-w-full shrink-0 flex-row',
      'gap-[10px] rounded-[50px] p-[5px]',
      'border border-solid border-neutral-lightest bg-white',
      'shadow-card',
      'overflow-x-auto',
      // Estilos para telas grandes (xl)
      'xl:fixed xl:top-[168px] xl:flex-col',
      className
    )}
    {...props}
  />
));
GuideNavWeek.displayName = 'GuideNavWeek';

const GuideNavWeekDay = React.forwardRef(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn(
      'c-guide-nav-week-day',
      // Estilos base
      'shrink-0',
      className
    )}
    {...props}
  />
));
GuideNavWeekDay.displayName = 'GuideNavWeekDay';

const guideNavWeekLinkVariants = cva('c-guide-nav-week-link inline-block p-[5px] rounded-full cursor-pointer', {
  variants: {
    isActive: {
      true: [
        'bg-secondary-lightest',
        // Texto dentro do link ativo
        '[&_text]:!fill-secondary-dark [&_text]:!font-bold',
        // Estilos do track do gráfico de pizza dentro do link ativo
        '[&_.c-guide-nav-week-track]:stroke-white'
      ]
    },
    colorScheme: {
      secondary: null,
      // Tema playful: dia ativo em laranja (--color-mission) em vez do roxo corporativo.
      mission: null
    }
  },
  compoundVariants: [
    {
      isActive: true,
      colorScheme: 'mission',
      class: ['!bg-mission-light', '[&_text]:!fill-mission']
    }
  ],
  defaultVariants: {
    isActive: false,
    colorScheme: 'secondary'
  }
});
const GuideNavWeekLink = React.forwardRef(({ className, isActive, colorScheme, ...props }, ref) => (
  <a
    ref={ref}
    className={cn(guideNavWeekLinkVariants({ isActive, colorScheme }), className)}
    {...props}
  />
));
GuideNavWeekLink.displayName = 'GuideNavWeekLink';

const GuideNavWeekProgress = React.forwardRef(({ className, ...props }, ref) => (
  <svg
    ref={ref}
    className={cn(
      'c-guide-nav-week-progress',
      'size-11 cursor-pointer',
      // Estilos para o círculo e texto, aplicados a partir do elemento pai
      '[&_circle]:transition-[stroke-dashoffset] [&_circle]:duration-150',
      '[&_text]:fill-neutral [&_text]:text-sm [&_text]:font-light',
      '[&_text]:[dominant-baseline:middle] [&_text]:[text-anchor:middle]',
      // Estilos de hover e focus para o texto
      '[&_text]:hover:fill-black [&_text]:focus:fill-black',
      '[&_text]:hover:font-bold [&_text]:focus:font-bold',
      className
    )}
    {...props}
  />
));
GuideNavWeekProgress.displayName = 'GuideNavWeekProgress';

const GuideNavWeekCircles = React.forwardRef(({ className, ...props }, ref) => (
  <g
    ref={ref}
    className={cn('c-guide-nav-week-circles', 'origin-center -rotate-90', className)}
    {...props}
  />
));
GuideNavWeekCircles.displayName = 'GuideNavWeekCircles';

const GuideNavWeekCircle = React.forwardRef(({ className, colour = 'var(--color-secondary)', percentage, size = 200, strokeWidth = 4, strokeLineCap = 'butt', ...props }, ref) => {
  const center = +size / 2;
  const radius = center - strokeWidth / 2;
  const dashArray = 2 * Math.PI * radius;
  const strokePercentage = ((100 - percentage) * dashArray) / 100; // where stroke will start, e.g. from 15% to 100%.

  return (
    <circle
      ref={ref}
      r={radius}
      cx={center}
      cy={center}
      fill="transparent"
      stroke={colour}
      strokeWidth={strokeWidth}
      strokeDasharray={dashArray}
      strokeDashoffset={percentage ? strokePercentage : 0}
      strokeLinecap={strokeLineCap}
      className={cn('c-guide-nav-week-circle', className)}
      {...props}
    />
  );
});

export { GuideNavWeek, GuideNavWeekDay, GuideNavWeekLink, GuideNavWeekProgress, GuideNavWeekCircles, GuideNavWeekCircle };
